import { ContentStateProperty, Gender } from '../../../@types/types'
import { ValidationRule } from '../../../@types/validation-types'
import { isValidPhoneNumber } from '../phoneNumberValidator/validatePhoneNumber'
import { isDateInThePast as isDateInThePastUtil } from '../../../utils/date-utils'

import dayjs from 'dayjs'
import { getLogger } from '../../logger/logger'

const DECIMAL_NUMBER = /^[-]?(\d+|\d{1,3}(\.\d{3})+)(,\d{1,2})?$/
const DECIMAL_NUMBER_WITH_ONE_DECIMAL_PLACE = /^[-]?(\d+|\d{1,3}(\.\d{3})+)(,\d{1})?$/
const ZERO_OR_MORE_WHITESPACES = /^\s*$/
const INTEGER_NUMBER_WITHOUT_SIGN = /^(0|[1-9]\d*)$/
const INTEGER_NUMBER_WITH_SEPARATOR = /^[-]?(\d+|\d{1,3}(\.\d{3}))?$/

// https://stackoverflow.com/a/1373724/8851223 (modified with umlaute block allowed in domain name - see üäöß)
// Close enough to the CLB backend validation, only a few tests are commented out!
const EMAIL_REGEX =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[äöüßa-z0-9](?:[äöüßa-z0-9-]*[äöüßa-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?$/

export function parseNumber(value: string | number | undefined | null): number {
  if (typeof value === 'number') {
    return value
  }

  if (!value) {
    return 0
  }

  return Number(value.split('.').join('').replace(',', '.'))
}

// REQUIRED FIELD VALIDATION RULES
export const hasNoValue: ValidationRule = (value: string) =>
  ZERO_OR_MORE_WHITESPACES.test(value) ? 'fieldRequired' : undefined

export const hasNoValueOrNoInformation: ValidationRule = (value: string) =>
  ZERO_OR_MORE_WHITESPACES.test(value) || value === 'NO_INFORMATION' ? 'fieldRequired' : undefined

export const hasNoValueForProperty: ValidationRule = (value, property) =>
  ZERO_OR_MORE_WHITESPACES.test(value) ? property + 'Required' : undefined

export const isRequiredWhenHasCourtage: ValidationRule = (value, property, insertionData) =>
  insertionData?.hasCourtage ? hasNoValue(value, property, insertionData) : undefined

export const isRequiredWhenHeatingCostsNotIncluded: ValidationRule = (value, property, insertionData) =>
  insertionData?.heatingCostsOption === false ? hasNoValue(value, property, insertionData) : undefined

// NUMBER VALIDATION RULES
export const isInvalidNumber: ValidationRule = (value: string) =>
  value != '' && !/^-?\d+$/.test(value) ? 'invalidFormat' : undefined

export const isNumberLessThan = (limit: number): ValidationRule =>
  function isNumberLessThan(value) {
    return parseNumber(value) < limit ? 'numberTooSmall' : undefined
  }
export const isNumberGreaterThan = (limit: number): ValidationRule =>
  function isNumberGreaterThan(value) {
    return parseNumber(value) > limit ? 'numberTooLarge' : undefined
  }
export const isNumberEqualOrLessThanZero: ValidationRule = (value: string) =>
  value != '' && parseNumber(value) <= 0 ? 'numberTooSmall' : undefined

export const isNumberNotDividableByZeroPointFive: ValidationRule = (value: string) =>
  value != '' && parseNumber(value) % 0.5 !== 0 ? 'invalidDecimal' : undefined

// INTEGER NUMBER VALIDATION RULES
export const isInvalidIntegerNumber: ValidationRule = (value: string) =>
  value != '' && !INTEGER_NUMBER_WITHOUT_SIGN.test(value) ? 'invalidFormat' : undefined

export const isInvalidIntegerWithSeparator: ValidationRule = (value: string) =>
  value != '' && !INTEGER_NUMBER_WITH_SEPARATOR.test(value) ? 'invalidFormat' : undefined

// DECIMAL NUMBER VALIDATION RULES
export const isInvalidDecimalNumber: ValidationRule = (value: string) =>
  value != '' && !DECIMAL_NUMBER.test(value) ? 'invalidDecimalFormat' : undefined

export const isInvalidDecimalNumberWithOneDecimalPlace: ValidationRule = (value: string) =>
  value != '' && !DECIMAL_NUMBER_WITH_ONE_DECIMAL_PLACE.test(value) ? 'invalidDecimalFormat' : undefined

// FIELD SPECIFIC VALIDATION RULES
export const isGarageGreaterThan = (limit: number): ValidationRule =>
  function isGarageGreaterThan(value) {
    return parseNumber(value) > limit ? 'garageTooLarge' : undefined
  }

export const isInvalidPostcode: ValidationRule = (value: string) =>
  value && !/^\d{5}$/.test(value) ? 'postcodeInvalidFormat' : undefined

export const isInvalidOptionalYear: ValidationRule = (value, type, insertionData) =>
  !hasNoValue(value, type, insertionData) && !/^[1-9]\d{3}$/.test(value) ? 'numberTooSmall' : undefined

export const isInvalidGender: ValidationRule = (value: string) =>
  value != Gender.MALE && value != Gender.FEMALE && value != Gender.MALE_OR_FEMALE ? 'genderRequired' : undefined

export const containsGreaterThanOrLessThanSign: ValidationRule = (value: string) =>
  value.includes('<') || value.includes('>') ? 'invalidSpecialCharacter' : undefined

export const isCharsLengthGreaterThan = (limit: number): ValidationRule =>
  function isCharsLengthGreaterThan(value) {
    return value.length > limit ? `moreThan${limit}Chars` : undefined
  }

export const isInvalidFlatShareSize: ValidationRule = (value: string) => {
  const flatShareSize = value != '' ? parseNumber(value) : undefined
  return flatShareSize !== undefined && (flatShareSize > 11 || flatShareSize < 2) ? 'invalidFlatShareSize' : undefined
}

export const isLargerThanTotalFloors: ValidationRule = (value, _property, insertionData) =>
  insertionData?.totalFloorsNumber &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.totalFloorsNumber.toString()) &&
  parseNumber(value) > parseNumber(insertionData.totalFloorsNumber.toString())
    ? 'fieldIsLargerThanTotalFloorsNumber'
    : undefined

export const isSmallerThanFloorField: ValidationRule = (value, _property, insertionData) =>
  insertionData &&
  insertionData.floor &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.floor.toString()) &&
  parseNumber(value) < parseNumber(insertionData.floor.toString())
    ? 'fieldIsSmallerThanFloorField'
    : undefined

export const isDependentFieldTotalFloorsNumberNotGiven: ValidationRule = (value, _property, insertionData) =>
  value.length > 0 &&
  !!insertionData &&
  (insertionData.totalFloorsNumber === undefined || insertionData.totalFloorsNumber === '')
    ? 'dependentFieldTotalFloorsNumberNotGiven'
    : undefined

export const isDependentFieldFloorNumberNotGiven: ValidationRule = (value, _property, insertionData) =>
  value.length > 0 && insertionData && (insertionData.floor === undefined || insertionData.floor === '')
    ? 'dependentFieldFloorNumberNotGiven'
    : undefined

export const isDependentFieldFreeUntilNotGiven: ValidationRule = (value, _property, insertionData) =>
  value.length > 0 && insertionData && (insertionData.freeUntil === undefined || insertionData.freeUntil === null)
    ? 'dependentFieldFreeUntilNotGiven'
    : undefined

export const isDependentFieldMoveInDateNotGiven: ValidationRule = (value, _property, insertionData) =>
  value.length > 0 && insertionData && (insertionData.moveInDate === undefined || insertionData.moveInDate === null)
    ? 'dependentFieldMoveInDateNotGiven'
    : undefined

export const isFreeUntilBeforeMoveInDate: ValidationRule = (value, _property, insertionData) =>
  insertionData?.moveInDate &&
  insertionData?.freeUntil &&
  dayjs(insertionData.freeUntil).isBefore(insertionData.moveInDate)
    ? 'fieldIsMoveInDateAfterFreeUntil'
    : undefined

export const isSmallerThanAgeOfRequestedFromField: ValidationRule = (value, _property, insertionData) =>
  insertionData &&
  insertionData.ageOfRequestedFrom &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.ageOfRequestedFrom.toString()) &&
  parseNumber(value) < parseNumber(insertionData.ageOfRequestedFrom.toString())
    ? 'fieldIsSmallerThanAgeFromField'
    : undefined

export const isLargerThanAgeOfRequestedToField: ValidationRule = (value, _property, insertionData) =>
  insertionData &&
  insertionData.ageOfRequestedTo &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.ageOfRequestedTo.toString()) &&
  parseNumber(value) > parseNumber(insertionData.ageOfRequestedTo.toString())
    ? 'fieldIsLargerThanAgeToField'
    : undefined

export const isSmallerThanMinRentalTimeField: ValidationRule = (value, _property, insertionData) =>
  insertionData &&
  insertionData.minRentalTime &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.minRentalTime.toString()) &&
  parseNumber(value) < parseNumber(insertionData.minRentalTime.toString())
    ? 'fieldIsSmallerThanMinRentalTimeField'
    : undefined

export const isLargerThanMaxRentalTimeField: ValidationRule = (value, _property, insertionData) =>
  insertionData &&
  insertionData.maxRentalTime &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.maxRentalTime.toString()) &&
  parseNumber(value) > parseNumber(insertionData.maxRentalTime.toString())
    ? 'fieldIsLargerThanMaxRentalTimeField'
    : undefined

export const isSmallerThanAgeOfFlatMatesFromField: ValidationRule = (value, _property, insertionData) =>
  insertionData &&
  insertionData.ageOfFlatMatesFrom &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.ageOfFlatMatesFrom.toString()) &&
  parseNumber(value) < parseNumber(insertionData.ageOfFlatMatesFrom.toString())
    ? 'fieldIsSmallerThanAgeFromField'
    : undefined

export const isLargerThanAgeOfFlatMatesToField: ValidationRule = (value, _property, insertionData) =>
  insertionData &&
  insertionData.ageOfFlatMatesTo &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(value) &&
  INTEGER_NUMBER_WITHOUT_SIGN.test(insertionData.ageOfFlatMatesTo.toString()) &&
  parseNumber(value) > parseNumber(insertionData.ageOfFlatMatesTo.toString())
    ? 'fieldIsLargerThanAgeToField'
    : undefined

export const REQUIRED_VALIDATORS = [hasNoValue, hasNoValueForProperty, isInvalidGender, hasNoValueOrNoInformation]

export const isInvalidPhoneNumberString: ValidationRule = (value: string) => {
  if (!isValidPhoneNumber(value)) return 'phoneNumberInvalidFormat'
  return undefined
}

export const isInvalidEmailAddress: ValidationRule = (value: string) =>
  !EMAIL_REGEX.test(value) ? 'emailInvalidFormat' : undefined

export const ifStringWithNumberThenGreaterThanZero: ValidationRule = (value: string) => {
  if (value === undefined || value === '') return undefined
  const number = Number(value)
  if (isNaN(number)) return undefined
  if (number > 0) return undefined
  return 'numberTooSmall'
}

export const isDateInThePast = (value: string) => {
  if (!value) return undefined

  return isDateInThePastUtil(new Date(value)) ? 'dateFieldIsBeforeToday' : undefined
}

export const validateZuoraForbiddenCharactersForField = (value: string) => {
  if (value?.includes('./') || value?.includes('\u00B4')) {
    return 'invalidSpecialCharacter'
  }
  return undefined
}

const popularEmailRegex =
  /(@|\[at]| at | Klammerraffe | \[at] )(gmail|yahoo|web|gmx|live|mail|posteo|t-online|outlook|mtl|freenet|fn|bossmail|freenetmail|justmail)\.(com|de|net|it|ru|fr|at|pl)/
const popularGlobalDomains = ['.com', '.de', '.net', '.org', '.biz', '.mil', '.ru', '.uk', '.fr', '.it', '.pl']

export function validateContainsEmail(value: unknown, property: ContentStateProperty) {
  if (property.includes('email')) {
    return undefined
  }

  if (typeof value !== 'string') {
    return undefined
  }

  if (value.includes('@')) {
    getLogger('EmailValidator').info(`Email detected in field [${property}] with value [${value}] [@]`)
    return 'fieldContainsEmailAddress'
  }

  if (value.match(popularEmailRegex)) {
    getLogger('EmailValidator').info(`Email detected in field [${property}] with value [${value}] [regex]`)
    return 'fieldContainsEmailAddress'
  }

  if (popularGlobalDomains.some((domain) => value.includes(domain))) {
    getLogger('EmailValidatorPotential').info(
      `Potential email detected in field [${property}] with value [${value}] [global domain]`
    )
    return undefined //it is only potential, we should not block before checking
  }

  return undefined
}
